"use client";

import * as React from "react";
import { Funnel as Filter, Plus, X } from 'lucide-react';

import { Button } from "@/components/ui/button";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
  ProductSortDropdown,
  ProductsFilter,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Link } from "@/components/common/Link";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: any;

function ProductsGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages,
    setPage,
    loadMore,
    isRefetching,
  } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            data-index={i}
            className="aspect-[3/4] bg-gray-100 animate-pulse rounded-lg"
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-gray-300 p-8 text-center rounded-lg">
        <p className="font-sans text-xs uppercase tracking-widest text-gray-500 mb-4">
          Connection Error
        </p>
        <p className="font-sans text-black mb-6">
          Unable to retrieve product data at this time.
        </p>
        <Button
          onClick={refetch}
          variant="outline"
          className="font-sans text-xs uppercase tracking-widest border-[#e63946] text-[#e63946] hover:bg-[#e63946] hover:text-white rounded-lg"
        >
          Retry Connection
        </Button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="border border-gray-300 p-12 text-center rounded-lg">
        <p className="font-sans text-xs uppercase tracking-widest text-gray-500 mb-2">
          No Results
        </p>
        <p className="font-sans text-black">
          No products match your current selection.
        </p>
      </div>
    );
  }

  
  const pageNumbers: number[] = [];
  const maxButtons = 5;
  let startPage = Math.max(1, currentPage - 2);
  let endPage = Math.min(totalPages, startPage + maxButtons - 1);
  if (endPage - startPage < maxButtons - 1) {
    startPage = Math.max(1, endPage - maxButtons + 1);
  }
  for (let p = startPage; p <= endPage; p++) pageNumbers.push(p);

  return (
    <>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => {
          const isDiscounted =
            product.discounted_price !== undefined &&
            product.discounted_price !== null &&
            product.discounted_price < product.price;

          return (
            <article
              key={product.id}
              data-index={i}
              className="group relative bg-white border border-gray-200 transition-all duration-200 hover:-translate-y-1 shadow-md rounded-lg overflow-hidden"
            >
              {}
              {isDiscounted && (
                <div className="absolute top-3 left-3 z-10 bg-[#e63946] text-white px-2 py-1 font-sans text-[10px] uppercase tracking-widest font-bold rounded-md">
                  Sale
                </div>
              )}

              {}
              <Link to={product.link} className="block">
                <div className="aspect-square bg-gray-50 overflow-hidden border-b border-gray-200">
                  {product.featured_image_url ? (
                    <img
                      src={product.featured_image_url}
                      alt={product.title}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center bg-gray-100">
                      <span className="font-sans text-[10px] uppercase tracking-widest text-gray-400">
                        No Image
                      </span>
                    </div>
                  )}
                </div>
              </Link>

              {}
              <div className="p-4 space-y-3">
                <div className="font-sans text-[10px] uppercase tracking-widest text-gray-500">
                  SKU-{String(product.id).padStart(4, "0")}
                </div>

                <Link to={product.link}>
                  <h3 className="font-default font-bold uppercase tracking-tight text-black text-base leading-tight hover:text-[#e63946] transition-colors">
                    {product.title}
                  </h3>
                </Link>

                {product.short_description && (
                  <div
                    className="font-default text-sm text-gray-600 line-clamp-1 [&_p]:mb-0 [&_p:last-child]:mb-0"
                    dangerouslySetInnerHTML={{ __html: product.short_description }}
                  />
                )}

                <div className="flex items-center justify-between pt-3 border-t border-gray-200 gap-3">
                  <div className="flex items-baseline gap-2">
                    {isDiscounted ? (
                      <>
                        <span className="font-default font-bold text-lg text-[#e63946]">
                          ${product.discounted_price?.toFixed(2)}
                        </span>
                        <span className="font-default text-xs text-gray-400 line-through">
                          ${product.price.toFixed(2)}
                        </span>
                      </>
                    ) : (
                      <span className="font-default font-bold text-lg text-black">
                        ${product.price.toFixed(2)}
                      </span>
                    )}
                  </div>

                  <AddToCartButton
                    productId={product.id}
                    variant="default"
                    size="sm"
                    buttonClassName="bg-[#e63946] text-white hover:bg-[#c62828] font-sans text-[10px] uppercase tracking-widest font-bold px-3 rounded-full"
                    text="Add"
                    showQuantity={false}
                  />
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {}
      <div className="mt-12 flex flex-col items-center gap-6">
        {hasNext && (
          <Button
            onClick={async () => await loadMore()}
            disabled={isRefetching}
            variant="outline"
            className="font-sans text-xs uppercase tracking-widest border-2 border-black bg-transparent text-black hover:bg-[#e63946] hover:text-white hover:border-[#e63946] px-8 py-6 rounded-lg"
          >
            {isRefetching ? "Loading..." : "Load More"}
          </Button>
        )}

        {totalPages > 1 && (
          <div className="flex items-center gap-2 flex-wrap justify-center">
            <Button
              onClick={prevPage}
              disabled={!hasPrev}
              variant="outline"
              size="sm"
              className="font-sans text-xs uppercase tracking-widest border-gray-300 bg-transparent text-black hover:bg-[#e63946] hover:text-white hover:border-[#e63946] rounded-md"
            >
              Prev
            </Button>

            {pageNumbers.map((p, idx) => (
              <Button
                key={p}
                data-index={idx}
                onClick={() => setPage(p)}
                variant="outline"
                size="sm"
                className={
                  p === currentPage
                    ? "font-sans text-xs font-bold bg-[#e63946] text-white border-[#e63946] hover:bg-[#e63946] hover:text-white min-w-9 rounded-md"
                    : "font-sans text-xs border-gray-300 bg-transparent text-black hover:bg-gray-100 hover:text-black min-w-9 rounded-md"
                }
              >
                {p}
              </Button>
            ))}

            <Button
              onClick={nextPage}
              disabled={!hasNext}
              variant="outline"
              size="sm"
              className="font-sans text-xs uppercase tracking-widest border-gray-300 bg-transparent text-black hover:bg-[#e63946] hover:text-white hover:border-[#e63946] rounded-md"
            >
              Next
            </Button>
          </div>
        )}

        {totalPages > 0 && (
          <p className="font-sans text-[10px] uppercase tracking-widest text-gray-500">
            Page {currentPage} / {totalPages}
          </p>
        )}
      </div>
    </>
  );
}

export default function Shop() {
  const current_wp_query_params = wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(current_wp_query_params);

  return (
    <section data-section-id="2296"
      id="shop-content"
      className="bg-white text-black py-16 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-7xl mx-auto">
        {}
        <div className="mb-10 border-b border-gray-200 pb-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-px w-8 bg-[#e63946]" />
            <span className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#e63946] font-bold">
              ProWhip Catalog
            </span>
          </div>
          <h2 className="font-default font-bold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-black">
            Premium Chargers
          </h2>
          <p className="font-default text-gray-600 mt-3 max-w-2xl">
            Professional-grade cream chargers for culinary perfection.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={wp_query}>
          <div className="flex gap-8">
            {}
            <aside className="hidden lg:block w-1/4 shrink-0">
              <div className="sticky top-24 space-y-6">
                <div className="flex items-center justify-between border-b border-gray-200 pb-3">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-[#e63946]" />
                    <h3 className="font-sans text-xs uppercase tracking-widest font-bold text-black">
                      Filters
                    </h3>
                  </div>
                </div>
                <ProductsFilter />
              </div>
            </aside>

            {}
            <div className="w-full lg:w-3/4 space-y-6">
              {}
              <div className="flex items-center justify-between gap-3 flex-wrap border-b border-gray-200 pb-4">
                <div className="flex items-center gap-3">
                  {}
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="lg:hidden font-sans text-xs uppercase tracking-widest border-2 border-black bg-transparent text-black hover:bg-[#e63946] hover:text-white hover:border-[#e63946] rounded-lg"
                      >
                        <Filter className="h-3.5 w-3.5 mr-2" />
                        Filters
                      </Button>
                    </SheetTrigger>
                    <SheetContent
                      side="left"
                      className="bg-white border-r border-gray-200 w-[85vw] sm:w-96 p-0 overflow-y-auto"
                    >
                      <SheetHeader className="p-6 border-b border-gray-200">
                        <SheetTitle className="font-default font-bold uppercase tracking-tight text-black flex items-center gap-2">
                          <Filter className="h-4 w-4 text-[#e63946]" />
                          Filters
                        </SheetTitle>
                      </SheetHeader>
                      <div className="p-6">
                        <ProductsFilter />
                      </div>
                    </SheetContent>
                  </Sheet>

                  <span className="font-sans text-[10px] uppercase tracking-widest text-gray-500 hidden sm:inline">
                    Browse Products
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <span className="font-sans text-[10px] uppercase tracking-widest text-gray-500 hidden sm:inline">
                    Sort
                  </span>
                  <ProductSortDropdown label="" />
                </div>
              </div>

              <ProductsGridConsumer />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}